import React, { useEffect, useRef, useState } from "react";
import './Energy-consumption.css';
import * as echarts from "echarts";

const CHART_CONFIG = {
  tooltipDecimals: 3,
  yAxisDecimals: 0,
  yAxisPadding: 0.1,
  barWidth: '30%',
  lineColor: "#8A0194",
};
const energyFormatter = (value) => {
  return Number.parseFloat((value / 1000).toFixed(2)); // Convert to thousands and format to 2 decimal places
}

const EnergyConsumption = ({ data }) => {
  const chartRef = useRef(null);

  console.log("Energy Consumption received chartData:", data);

  useEffect(() => {
    // Validate that we have the chart container and data
    if (!chartRef.current) {
      console.log("Chart ref not available");
      return;
    }

    if (!data || (Array.isArray(data) && data.length === 0) || (typeof data === 'object' && Object.keys(data).length === 0)) {
      console.log("No data available for chart", data);
      return;
    }

    console.log("Initializing chart with data:", data);

    let chartInstance = echarts.init(chartRef.current);
    let resizeObserver;

    const isCompactView = chartRef.current?.offsetWidth < 500;

    const option = {
      tooltip: {
        trigger: 'axis',
        appendToBody: true,
        confine: false,
        backgroundColor: 'white',
        borderColor: '#ccc',
        borderWidth: 1,
        extraCssText: 'z-index: 9999;',
        textStyle: { color: '#333', fontSize: 14 },
      },
      legend: {
        type: isCompactView ? 'scroll' : 'plain',
        data: ['BHKW 1', 'BHKW 2', 'PV1', 'PV2', 'Netzbezug', 'Einspeisung'],
        top: isCompactView ? 8 : 5,
        left: 'center',
        itemWidth: isCompactView ? 10 : 14,
        itemHeight: isCompactView ? 10 : 14,
        textStyle: { fontSize: isCompactView ? 10 : 12 }
      },
      xAxis: {
        type: 'category',
        data: data.timestamp || [0],
        name: 'Time',
        nameLocation: 'middle',
        nameGap: 25,
        axisLabel: { fontSize: 12, color: '#333', rotate: 0, interval: 'auto' },
        axisLine: { show: true, lineStyle: { color: '#333' } },
        axisTick: { show: true, alignWithLabel: true },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        name: 'Consumption (Tsd.)',
        nameLocation: 'middle',
        nameGap: 55,
        nameTextStyle: { color: 'black', fontSize: 12 },
        axisLabel: { show: true, fontSize: 11, color: '#333' },
        axisLine: { show: true, lineStyle: { color: '#333' } },
        axisTick: { show: true },
        splitLine: { show: true, lineStyle: { type: 'dashed', color: '#ccc' } },
      },
      series: [
        {
          name: 'BHKW 1',
          type: 'bar',
          stack: 'one',
          barWidth: CHART_CONFIG.barWidth,
          itemStyle: { color: '#005CA3' },
          data: data.BHKW_Generator1_71282_Energie ? data.BHKW_Generator1_71282_Energie.map(item => energyFormatter(item.value || 0)) : [0],
        },
        {
          name: 'BHKW 2',
          type: 'bar',
          stack: 'one',
          barWidth: CHART_CONFIG.barWidth,
          itemStyle: { color: '#51A34B' },
          data: data.BHKW_Generator2_71284_Energie ? data.BHKW_Generator2_71284_Energie.map(item => energyFormatter(item.value || 0)) : [0],
        },
        {
          name: 'PV1',
          type: 'bar',
          stack: 'one',
          barWidth: CHART_CONFIG.barWidth,
          itemStyle: { color: '#A13FA1' },
          data: data.Verwaltungsgebaude_UG_Station4_PV_1951063_Energie ? data.Verwaltungsgebaude_UG_Station4_PV_1951063_Energie.map(item => energyFormatter(item.value || 0)) : [0],
        },
        {
          name: 'PV2',
          type: 'bar',
          stack: 'one',
          barWidth: CHART_CONFIG.barWidth,
          itemStyle: { color: '#339af0' },
          data: data.Empfang_PV_Anlage_UV_rechts_oben_1395203 ? data.Empfang_PV_Anlage_UV_rechts_oben_1395203.map(item => energyFormatter(item.value || 0)) : [0],
        },
        {
          name: 'Netzbezug',
          type: 'bar',
          stack: 'one',
          barWidth: CHART_CONFIG.barWidth,
          itemStyle: { color: '#96f2d7' },
          data: data.BHKW_Gesamtbezug_ABB_Wirk_Plus_1_Energie ? data.BHKW_Gesamtbezug_ABB_Wirk_Plus_1_Energie.map(item => energyFormatter(item.value || 0)) : [0],
        },
        {
          name: 'Einspeisung',
          type: 'bar',
          stack: 'two',
          barWidth: CHART_CONFIG.barWidth,
          itemStyle: { color: '#88B04B' },
          data: data.BHKW_Gesamteinspeisung_ABB_Wirk_Minus_2_Energie ? data.BHKW_Gesamteinspeisung_ABB_Wirk_Minus_2_Energie.map(item => energyFormatter(item.value || 0)) : [0],
        }
      ],
      grid: {
        top: isCompactView ? 85 : 50,
        left: isCompactView ? 60 : '10%',
        right: isCompactView ? 20 : '5%',
        bottom: '15%',
        containLabel: true
      },
    };

    chartInstance.setOption(option);

    // Add ResizeObserver for better responsiveness
    resizeObserver = new ResizeObserver(() => {
      if (chartInstance) {
        chartInstance.resize();
      }
    });

    resizeObserver.observe(chartRef.current);

    const handleResize = () => chartInstance.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      chartInstance.dispose();
    };
  }, [data]);

  return (
    <div className="active-power-container">
      <div
        ref={chartRef}
        className="chart_activepower"
      />
    </div>
  );
};

export default EnergyConsumption;